import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import type {
  SolConversationRow,
  SolConversationSearchRow,
  SolMessageRow,
} from '../database.types';

/**
 * SOL CONVERSATION HISTORY — the reads. M11 / D-041.
 *
 * **TWO LISTS, ONE SEARCH, ONE RESTORE — ALL UNDER RLS, NONE PRIVILEGED.**
 *
 *   * The DEFAULT LIST is organized by ORIGIN: Global Sol shows Global-origin
 *     conversations; a Project shows conversations STARTED from that Project.
 *     Archived ones are hidden unless asked for. Newest activity first — by
 *     `updated_at`, which only `append_sol_turn` moves, so a title or summary
 *     maintained in the background never bumps an old chat to the top.
 *   * SEARCH is not siloed: Global search crosses every origin he may read;
 *     a Project's search stays Project-focused by default (D-041, J-7). It
 *     runs through `search_sol_conversations`, SECURITY INVOKER under RLS.
 *   * RESTORE reads the stored messages of ONE conversation, oldest first, in
 *     bounded pages — what puts the visible bubbles back after an app restart.
 *
 * **NONE OF THIS IS CONVERSATIONAL MEMORY FOR SOL.** The browser reads what
 * was said so a person can see it again; nothing read here is sent to Sol.
 * What Sol is shown is decided on the server, bounded, from the same rows.
 */

/** The exact columns the list needs. Never the context JSONB, never a star. */
export const SOL_CONVERSATION_LIST_COLUMNS =
  'id, owner_user_id, project_id, started_context, title, title_source, archived_at, created_at, updated_at, turn_count' as const;

export const SOL_MESSAGE_COLUMNS =
  'id, conversation_id, owner_user_id, seq, role, body, created_at' as const;

/** How many conversations one list read returns. A bounded screen, always. */
export const SOL_CONVERSATION_LIST_LIMIT = 50;
/** How many messages one restore page holds. */
export const SOL_MESSAGE_PAGE_SIZE = 60;

export type SolHistoryScope =
  { readonly kind: 'global' } | { readonly kind: 'project'; readonly projectId: string };

export type SolConversationListStatus = 'loading' | 'ready' | 'failed';

export interface SolConversationListState {
  readonly status: SolConversationListStatus;
  readonly conversations: readonly SolConversationRow[];
  readonly error: string | null;
  readonly reload: () => Promise<void>;
}

type ListFetch = { kind: 'ready'; rows: SolConversationRow[] } | { kind: 'failed' };

/**
 * The default list for one origin. Archived hidden unless `includeArchived`.
 */
export function useSolConversations(
  scope: SolHistoryScope,
  includeArchived: boolean,
): SolConversationListState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<SolConversationListStatus>(client ? 'loading' : 'failed');
  const [conversations, setConversations] = useState<readonly SolConversationRow[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );
  const scopeProjectId = scope.kind === 'project' ? scope.projectId : null;

  /** Pure fetch. Sets no state, so the effect decides when to apply it — the house pattern. */
  const fetchList = useCallback(async (): Promise<ListFetch> => {
    if (!client) return { kind: 'failed' };
    let query = client
      .from('sol_conversation')
      .select(SOL_CONVERSATION_LIST_COLUMNS)
      .order('updated_at', { ascending: false })
      .limit(SOL_CONVERSATION_LIST_LIMIT);
    // **ORIGIN ORGANIZES THE LIST.** A Global chat that discussed a Project
    // for an hour is still a Global chat; a Project's list is what was
    // STARTED there.
    query =
      scopeProjectId === null
        ? query.eq('started_context', 'global')
        : query.eq('project_id', scopeProjectId);
    if (!includeArchived) query = query.is('archived_at', null);

    const { data, error: queryError } = await query;
    if (queryError) {
      reportError(queryError, 'sol-conversations.load');
      return { kind: 'failed' };
    }
    return { kind: 'ready', rows: data ?? [] };
  }, [client, includeArchived, scopeProjectId]);

  const apply = useCallback((result: ListFetch) => {
    if (result.kind === 'failed') {
      setConversations([]);
      // **AN EMPTY LIST AND A FAILED LOAD ARE DIFFERENT FACTS.**
      setError('Could not load your conversations.');
      setStatus('failed');
      return;
    }
    setConversations(result.rows);
    setError(null);
    setStatus('ready');
  }, []);

  /** Only the newest read may speak — the house pattern. */
  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchList();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchList, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  return { status, conversations, error, reload: load };
}

/**
 * Deterministic conversation search. **Global search crosses origins**
 * (`scope: 'all'`); a Project search stays Project-focused by default.
 * Returns rows ranked by the database; nothing here re-ranks.
 */
export async function searchSolConversations(input: {
  readonly query: string;
  readonly scope: SolHistoryScope;
  readonly includeArchived: boolean;
}): Promise<readonly SolConversationSearchRow[]> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Crewlink is not connected to a server yet.');
  const trimmed = input.query.trim();
  if (trimmed.length === 0) return [];
  const { data, error } = await client.rpc('search_sol_conversations', {
    p_query: trimmed,
    p_scope: input.scope.kind === 'global' ? 'all' : 'project',
    p_project_id: input.scope.kind === 'project' ? input.scope.projectId : null,
    p_include_archived: input.includeArchived,
    p_boost_project_id: input.scope.kind === 'project' ? input.scope.projectId : null,
    p_limit: SOL_CONVERSATION_LIST_LIMIT,
  });
  if (error) throw error;
  return data ?? [];
}

/** One restored message — exactly what was typed, exactly what Sol showed. */
export interface SolRestoredMessage {
  readonly seq: number;
  readonly role: 'user' | 'sol';
  readonly body: string;
}

/**
 * The NEWEST page of one conversation's messages, oldest first within the
 * page. `beforeSeq` walks back for an older page. **Bounded on the query**, so
 * a long conversation is never read whole to draw a screen.
 */
export async function loadSolConversationMessages(input: {
  readonly conversationId: string;
  readonly beforeSeq?: number | null;
  readonly pageSize?: number;
}): Promise<{ readonly messages: readonly SolRestoredMessage[]; readonly hasOlder: boolean }> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Crewlink is not connected to a server yet.');
  const pageSize = input.pageSize ?? SOL_MESSAGE_PAGE_SIZE;
  let query = client
    .from('sol_message')
    .select(SOL_MESSAGE_COLUMNS)
    .eq('conversation_id', input.conversationId)
    .order('seq', { ascending: false })
    .limit(pageSize + 1);
  if (input.beforeSeq !== undefined && input.beforeSeq !== null) {
    query = query.lt('seq', input.beforeSeq);
  }
  const { data, error } = await query;
  if (error) throw error;
  const rows = (data ?? []) as SolMessageRow[];
  const hasOlder = rows.length > pageSize;
  const page = rows.slice(0, pageSize).reverse();
  return {
    messages: page.map((row) => ({
      seq: row.seq,
      role: row.role === 'sol' ? 'sol' : 'user',
      body: row.body,
    })),
    hasOlder,
  };
}

/** One conversation's row, for a header — or null when it is not the caller's. */
export async function loadSolConversation(
  conversationId: string,
): Promise<SolConversationRow | null> {
  const client = getSupabaseClient();
  if (!client) return null;
  const { data, error } = await client
    .from('sol_conversation')
    .select(SOL_CONVERSATION_LIST_COLUMNS)
    .eq('id', conversationId)
    .maybeSingle();
  if (error) {
    reportError(error, 'sol-conversation.load');
    return null;
  }
  return data ?? null;
}
